import type { createBrowserRouter } from 'react-router-dom'
import { stopRecordingBeforeSecretNavigation } from '@/bayawatchPrivacy'

type DeepLinkRouter = Pick<
  ReturnType<typeof createBrowserRouter>,
  'state' | 'navigate' | 'subscribe'
>

/**
 * Native push tap / universal link → in-app navigation.
 *
 * Performed only once the router has finished its INITIAL load and is idle. On
 * a cold start (the app was quit and the tap launches it), the deep link can
 * arrive while the router is still resolving the initial route +
 * `ProtectedRoute`'s auth fetch. Navigating during that window races the auth
 * check: `ProtectedRoute` briefly sees no user and hard-redirects via
 * `window.location.href='/login'`, which on the `capacitor://localhost` WebView
 * reloads to a blank/white screen instead of landing on the issue. Waiting for
 * `initialized` + idle guarantees the session is resolved before we navigate.
 *
 * A deep link can point at a token route (a shared report link tapped in an
 * email opens the app), so session recording is stopped first while the old URL
 * is still current — see `stopRecordingBeforeSecretNavigation`.
 */
export function navigateToDeepLink(router: DeepLinkRouter, path: string): void {
  const go = () => {
    stopRecordingBeforeSecretNavigation(path)
    void router.navigate(path)
  }
  if (router.state.initialized && router.state.navigation.state === 'idle') {
    go()
    return
  }
  const unsubscribe = router.subscribe(state => {
    if (state.initialized && state.navigation.state === 'idle') {
      unsubscribe()
      go()
    }
  })
}
